/**
 * Reloj del negocio: zona FIJA UTC−3 (docs/esquema.md §3), independiente de la
 * zona configurada en el teléfono. Todo el estado interno trabaja en ms epoch;
 * los strings locales (`YYYY-MM-DD`, `YYYY-MM-DDTHH:MM`) se generan solo para
 * el wire y la pantalla. El desvío medido contra hora_servidor se suma al
 * reloj local para obtener «ahora» calibrado.
 */

const OFFSET_NEGOCIO_MS = -3 * 60 * 60 * 1000;

function partes(ms: number): Date {
	// Date en UTC desplazada al huso del negocio: los getUTC* dan la hora local.
	return new Date(ms + OFFSET_NEGOCIO_MS);
}

function dosDigitos(n: number): string {
	return String(n).padStart(2, '0');
}

/** `YYYY-MM-DDTHH:MM` local del negocio (formato wire de corte_en/entrada_en). */
export function instanteLocal(ms: number): string {
	const d = partes(ms);
	return (
		`${d.getUTCFullYear()}-${dosDigitos(d.getUTCMonth() + 1)}-${dosDigitos(d.getUTCDate())}` +
		`T${dosDigitos(d.getUTCHours())}:${dosDigitos(d.getUTCMinutes())}`
	);
}

/** El camino inverso de `instanteLocal`: `YYYY-MM-DDTHH:MM` del negocio → ms. */
export function msDeInstanteLocal(instante: string): number {
	return Date.parse(`${instante}:00.000-03:00`);
}

/** `YYYY-MM-DD` local del negocio. Día CALENDARIO: casi nunca es lo que se quiere. */
export function fechaLocal(ms: number): string {
	return instanteLocal(ms).slice(0, 10);
}

/**
 * La jornada de la planta NO termina a medianoche: corre de 06:00 a 06:00.
 * La responsable lo dijo así (1/9/2026): «trabajamos 24 horas… yo considero que
 * es de 6 a 30 horas, es decir, las 5:00 am para mí son las 29 hs».
 *
 * Se verificó contra las 270 filas de 2025: los 16 viajes que entran entre las
 * 00:00 y las 02:06 están anotados al final de su día en la planilla, no al
 * principio del siguiente. El Excel siempre usó este criterio.
 *
 * ESTA es la fecha que va al campo `fecha` del viaje, y la MISMA que elige la
 * tara vigente del carro — si se separaran, el servidor validaría la tara con
 * una fecha distinta de la que usó el cliente y rechazaría viajes legítimos.
 */
const CORTE_JORNADA_MS = 6 * 60 * 60 * 1000;

export function fechaDeProduccion(ms: number): string {
	return fechaLocal(ms - CORTE_JORNADA_MS);
}

/**
 * La hora en el reloj de la jornada: `00:48` → `24:48`, `05:00` → `29:00`.
 * Solo tiene sentido para un instante que pertenece a la jornada que se está
 * mostrando — para `corte_en`, que puede ser de otro día, usar `horaCorta`.
 */
export function horaDeJornada(ms: number): string {
	const hhmm = horaCorta(ms);
	const horas = Number(hhmm.slice(0, 2));
	return horas < 6 ? `${horas + 24}:${hhmm.slice(3)}` : hhmm;
}

/** `HH:MM` para pantalla. */
export function horaCorta(ms: number): string {
	return instanteLocal(ms).slice(11);
}

/**
 * «14:20» si es de hoy, «ayer 14:20» si fue ayer y «10/9 14:20» más atrás: en una
 * tarjeta, un silo abierto ayer no puede leerse como de hoy (revisión del 12/9). Con
 * `conHoy`, el de hoy también lleva su palabra («hoy 22:00»).
 */
export function cuandoCorto(instante: string, hoyMs: number, conHoy = false): string {
	const fecha = instante.slice(0, 10);
	const hora = instante.slice(11, 16);
	const hoy = fechaLocal(hoyMs);
	if (fecha === hoy) return conHoy ? `hoy ${hora}` : hora;
	if (fecha === fechaLocal(hoyMs - 24 * HORA_MS)) return `ayer ${hora}`;
	return `${Number(fecha.slice(8, 10))}/${Number(fecha.slice(5, 7))} ${hora}`;
}

export const MINUTO_MS = 60_000;
export const HORA_MS = 60 * MINUTO_MS;

/**
 * Las horas que el teléfono tiene para corregir lo que cargó (Agata, 15/10/2026: «48 hs para todos los datos»; §12.18),
 * contadas desde que llegó al servidor lo que se corrige: la carga, la hoja o la humedad. Lo hace cumplir el servidor
 * (`pasaron_48_h`); el teléfono ofrece corregir con el mismo borde, desde la hora del snapshot.
 */
export const HORAS_PARA_CORREGIR = 48;

/**
 * Los turnos de producción, fijos y con nombre (gerenta, 7/9): Vero de 6 a 14,
 * Eli de 14 a 22, Gise de 22 a 6. El turno se PROPONE desde la hora y se puede
 * cambiar — «alguna vez se cambian entre ellas, como excepción» —, así que esto
 * propone, no decide.
 *
 * El wire escribe `maniana` sin ñ (CHECK del DDL); la pantalla dice «mañana».
 */
export type Turno = 'maniana' | 'tarde' | 'noche';

export function turnoDe(ms: number): Turno {
	const hora = Number(horaCorta(ms).slice(0, 2));
	if (hora >= 6 && hora < 14) return 'maniana';
	if (hora >= 14 && hora < 22) return 'tarde';
	return 'noche';
}

/** Hora de arranque de cada turno, en el reloj del negocio. Gestión la usa para fechar una parada corregida (`fecha` + turno → `registrado_en`). */
export const ARRANQUE: Record<Turno, number> = { maniana: 6, tarde: 14, noche: 22 };

/**
 * El instante en que arrancó el turno que se está informando: el más reciente
 * que ya pasó. `parada.registrado_en` es ESO y no la hora del tipeo (DDL: «es el
 * INICIO del turno que se informa, no la hora del tipeo») — a las 06:10 se anota
 * la parada de la noche, que arrancó a las 22:00 de AYER, y si se guardara «ahora»
 * la parada caería en la jornada siguiente (Codex, ronda del plan, 8/9).
 */
export function inicioDelTurno(ahoraMs: number, turno: Turno): number {
	const d = new Date(ahoraMs + OFFSET_NEGOCIO_MS);
	const arranque = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), ARRANQUE[turno]) - OFFSET_NEGOCIO_MS;
	// Si ese arranque todavía no pasó, el turno vigente empezó el día anterior.
	return arranque <= ahoraMs ? arranque : arranque - 24 * HORA_MS;
}

export const TURNOS: Turno[] = ['maniana', 'tarde', 'noche'];
export const NOMBRE_TURNO: Record<Turno, string> = { maniana: 'mañana', tarde: 'tarde', noche: 'noche' };
export const HORARIO_TURNO: Record<Turno, string> = { maniana: '6 a 14', tarde: '14 a 22', noche: '22 a 6' };
